/**
 * Button — the only Button component in the system.
 *
 * Variants:
 *   - kind:  primary | secondary | ghost | danger | dark
 *   - size:  sm | md | lg
 *   - block: true → full-width
 *
 * Slots:
 *   - iconLeft, iconRight — pass any element (typically a Lucide icon)
 *   - loading — replaces icons with a spinner
 *
 * Accessibility:
 *   - Pass `aria-label` when only an icon is shown (no text children).
 *   - Disabled buttons keep pointer-events for tooltips but block onClick.
 *
 * Examples:
 *   <Button kind="primary" iconLeft={<Plus />}>Tạo mới</Button>
 *   <Button kind="ghost" size="sm" iconLeft={<Download />} aria-label="Tải CSV" />
 *   <Button kind="danger" loading>Đang xoá…</Button>
 */

import React from 'react';
import { Spinner } from './icons';

const KIND_CLASSES = {
  primary:
    'bg-teal-600 text-white border border-teal-600 hover:bg-teal-700 hover:border-teal-700 ' +
    'active:bg-teal-800 disabled:bg-stone-300 disabled:border-stone-300 disabled:text-white',
  secondary:
    'bg-white text-stone-900 border border-stone-300 hover:bg-stone-50 hover:border-stone-400 ' +
    'active:bg-stone-100 disabled:bg-stone-50 disabled:text-stone-400',
  ghost:
    'bg-transparent text-stone-600 border border-transparent hover:bg-stone-100 hover:text-stone-900 ' +
    'active:bg-stone-200 disabled:text-stone-400',
  danger:
    'bg-white text-red-600 border border-red-200 hover:bg-red-50 hover:border-red-300 ' +
    'active:bg-red-100 disabled:text-stone-400 disabled:border-stone-200',
  // High-contrast for teal-on-light pages where teal would compete with content
  dark:
    'bg-stone-900 text-white border border-stone-900 hover:bg-stone-800 ' +
    'active:bg-stone-950 disabled:bg-stone-300 disabled:border-stone-300',
};

const SIZE_CLASSES = {
  sm: 'h-7  px-2.5 text-xs   gap-1.5 rounded-md',
  md: 'h-8  px-3   text-sm   gap-1.5 rounded-md',
  lg: 'h-10 px-4   text-base gap-2   rounded-md',
};

const ICON_SIZE = { sm: 13, md: 14, lg: 16 };

export const Button = React.forwardRef(function Button(
  {
    kind = 'primary',
    size = 'md',
    block = false,
    iconLeft,
    iconRight,
    loading = false,
    disabled,
    className = '',
    children,
    type = 'button',
    ...rest
  },
  ref
) {
  const isIconOnly = !children && (iconLeft || iconRight || loading);
  const iconPx = ICON_SIZE[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={[
        'ams-focus-ring inline-flex items-center justify-center font-medium leading-none whitespace-nowrap',
        'transition-colors duration-150 ease-out select-none',
        'disabled:cursor-not-allowed',
        KIND_CLASSES[kind],
        SIZE_CLASSES[size],
        block && 'w-full',
        isIconOnly && '!px-0 ' + (size === 'sm' ? 'w-7' : size === 'md' ? 'w-8' : 'w-10'),
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {loading ? (
        <Spinner size={iconPx} className="animate-spin" />
      ) : (
        iconLeft && cloneWithSize(iconLeft, iconPx)
      )}
      {children}
      {!loading && iconRight && cloneWithSize(iconRight, iconPx)}
    </button>
  );
});

function cloneWithSize(node, px) {
  if (!React.isValidElement(node)) return node;
  return React.cloneElement(node, {
    size: node.props.size ?? px,
    'aria-hidden': true,
    focusable: 'false',
  });
}

/**
 * ButtonGroup — segmented control. Useful for view toggles (List | Grid),
 * sort orders, or tab-bar-style filters.
 *
 *   <ButtonGroup value={view} onChange={setView}
 *     options={[{ value:'list', label:'Danh sách' }, { value:'grid', label:'Lưới' }]} />
 */
export function ButtonGroup({ value, onChange, options, size = 'md', ariaLabel }) {
  const h = { sm: 'h-7', md: 'h-8', lg: 'h-10' }[size];
  const padX = { sm: 'px-2.5', md: 'px-3', lg: 'px-4' }[size];
  const text = { sm: 'text-xs', md: 'text-sm', lg: 'text-base' }[size];
  return (
    <div role="radiogroup" aria-label={ariaLabel}
      className="inline-flex items-center bg-stone-100 border border-stone-200 rounded-md p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            onClick={() => onChange?.(opt.value)}
            className={[
              'ams-focus-ring inline-flex items-center gap-1.5 font-medium rounded transition-all',
              h, padX, text,
              active
                ? 'bg-white text-stone-900 shadow-xs border border-stone-200'
                : 'bg-transparent text-stone-500 hover:text-stone-700 border border-transparent',
            ].join(' ')}
          >
            {opt.icon && cloneWithSize(opt.icon, ICON_SIZE[size])}
            {opt.label}
            {opt.count != null && (
              <span className="num text-2xs opacity-70 ml-0.5">{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** IconButton — convenience wrapper for icon-only buttons. */
export function IconButton({ icon, kind = 'ghost', size = 'md', ariaLabel, ...rest }) {
  return <Button kind={kind} size={size} iconLeft={icon} aria-label={ariaLabel} {...rest} />;
}
